import { useState } from 'react';
import { ownerService } from 'services/api';
import Modal from 'components/Modal';
import AvisoModal from 'components/AvisoModal';
import SearchableSelect from 'components/SearchableSelect';
import styles from './CambiarPropietarioModal.module.css';
import { EMAIL_REGEX } from 'utils/email';

const TIPOS_ID = ['CC', 'CC_DIG', 'NUIP'];
const PHONE_REGEX = /^\d{7,10}$/;
// Mismos topes que valida el backend (ownersController.js · traspasarUnidad).
const MAX_NOMBRE = 150;
const MAX_DOCUMENTO = 50;
const MAX_CORREO = 254;

// Con noValidate el navegador no bloquea el envío: se revisa aquí lo mismo que el servidor.
function validarCampos(form) {
  if (!form.unit_id) return 'Elige la unidad que cambia de propietario';
  if (!form.numero_identificacion.trim()) return 'El número de documento es requerido';
  if (form.numero_identificacion.trim().length > MAX_DOCUMENTO) return `El documento no puede tener más de ${MAX_DOCUMENTO} caracteres`;
  if (!form.nombre.trim()) return 'El nombre es requerido';
  if (form.nombre.trim().length > MAX_NOMBRE || form.apellido.trim().length > MAX_NOMBRE) return `El nombre y el apellido no pueden tener más de ${MAX_NOMBRE} caracteres`;
  if (!form.email.trim()) return 'El correo es requerido';
  if (form.email.trim().length > MAX_CORREO) return `El correo no puede tener más de ${MAX_CORREO} caracteres`;
  if (!EMAIL_REGEX.test(form.email.trim())) return 'El correo no tiene un formato válido';
  if (form.telefono.trim() && !PHONE_REGEX.test(form.telefono.trim())) return 'El teléfono no es válido — debe tener 7 (fijo) o 10 (celular) dígitos, sin espacios ni +57';
  return null;
}

const etiquetaUnidad = (u) => `Unidad ${u.numero_unidad}${u.building?.numero_torre ? ` · ${u.building.numero_torre}` : ''}`;

export default function CambiarPropietarioModal({ owner, unidades, onClose, onDone }) {
  const [form, setForm] = useState({
    unit_id: unidades.length === 1 ? String(unidades[0].id) : '',
    tipo_identificacion: 'CC', numero_identificacion: '', nombre: '', apellido: '', email: '', telefono: '',
  });
  const [paso, setPaso] = useState('datos');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const set = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }));
  const unidad = unidades.find(u => String(u.id) === String(form.unit_id));
  const nombreActual = `${owner.nombre} ${owner.apellido}`.trim();

  const continuar = (e) => {
    e.preventDefault();
    const error = validarCampos(form);
    if (error) { setModalError(error); return; }
    setModalError('');
    setPaso('confirmar');
  };

  const guardar = async () => {
    setSaving(true);
    setModalError('');
    try {
      const r = await ownerService.traspasarUnidad({
        unit_id: Number(form.unit_id),
        tipo_identificacion: form.tipo_identificacion,
        numero_identificacion: form.numero_identificacion.trim(),
        nombre: form.nombre.trim(), apellido: form.apellido.trim(),
        email: form.email.trim(), telefono: form.telefono.trim(),
      });
      onDone(r.data.message);
    } catch (err) {
      // El aviso va dentro del modal: la alerta de la página queda tapada detrás de él.
      setModalError(err.response?.data?.message || 'No se pudo cambiar el propietario. Inténtalo de nuevo.');
      setPaso('datos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={() => !saving && onClose()} maxWidth={620}>
      <div className="modal-header">
        <h3><i className={`fas fa-right-left ${styles.iconoTitulo}`} />Cambiar propietario</h3>
        <button className="btn-close" onClick={onClose} disabled={saving}>✕</button>
      </div>
      <form onSubmit={continuar} noValidate>
        <div className="modal-body">
          {modalError && (
            <AvisoModal>
              <i className={`fas fa-exclamation-circle ${styles.iconoError}`} />
              {modalError}
            </AvisoModal>
          )}
          {paso === 'datos' ? (
            <>
              <p className={styles.intro}>
                Úsalo cuando la unidad se vendió o pasó a otra persona. Si solo hay que corregir un dato de {nombreActual || 'esta persona'}, no es esto.
              </p>
              <div className="form-group">
                <label className="form-label">Unidad *</label>
                <SearchableSelect value={form.unit_id} onChange={(v) => setForm(f => ({ ...f, unit_id: v }))} minWidth={0} maxWidth={9999}
                  allowEmpty={false} placeholder="— Selecciona la unidad —" ariaLabel="Unidad"
                  options={unidades.map(u => ({ value: String(u.id), label: etiquetaUnidad(u) }))} />
              </div>
              <div className={styles.rejilla}>
                <div className="form-group">
                  <label className="form-label">Tipo de documento *</label>
                  <SearchableSelect value={form.tipo_identificacion} onChange={(v) => setForm(f => ({ ...f, tipo_identificacion: v }))} minWidth={0} maxWidth={9999}
                    allowEmpty={false} ariaLabel="Tipo de documento" options={TIPOS_ID.map(t => ({ value: t, label: t }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Número de documento *</label>
                  <input className="form-control" value={form.numero_identificacion} onChange={set('numero_identificacion')} maxLength={MAX_DOCUMENTO} autoComplete="off" />
                </div>
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input className="form-control" value={form.nombre} onChange={set('nombre')} maxLength={MAX_NOMBRE} autoComplete="off" />
                </div>
                <div className="form-group">
                  <label className="form-label">Apellido</label>
                  <input className="form-control" value={form.apellido} onChange={set('apellido')} maxLength={MAX_NOMBRE} autoComplete="off" />
                </div>
                <div className="form-group">
                  <label className="form-label">Correo *</label>
                  <input type="email" className="form-control" value={form.email} onChange={set('email')} maxLength={MAX_CORREO} autoComplete="off" />
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-control" value={form.telefono} onChange={set('telefono')} maxLength={10} inputMode="numeric" autoComplete="off" />
                </div>
              </div>
              <div className={styles.nota}>
                Si la persona ya está en el sistema (misma cédula), sus datos no se modifican: nombre y correo deben coincidir con los registrados.
              </div>
            </>
          ) : (
            <div className={styles.resumen}>
              <p className={styles.resumenLinea}>
                <strong>{unidad ? etiquetaUnidad(unidad) : 'La unidad'}</strong> pasa de <strong>{nombreActual || 'su propietario actual'}</strong> a{' '}
                <strong>{`${form.nombre.trim()} ${form.apellido.trim()}`.trim()}</strong> ({form.tipo_identificacion} {form.numero_identificacion.trim()}).
              </p>
              <p className={styles.resumenAviso}>
                Si {nombreActual || 'el propietario actual'} no tiene más unidades, su cuenta de acceso queda desactivada. Si la nueva persona no tenía cuenta, se crea y recibe el correo de bienvenida. No se puede hacer mientras la unidad esté en una asamblea en curso o con un poder dado.
              </p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          {paso === 'datos' ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary"><i className="fas fa-arrow-right" /> Continuar</button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPaso('datos')} disabled={saving}>Volver</button>
              <button type="button" className="btn btn-primary" onClick={guardar} disabled={saving}>
                {saving ? <><div className="spinner" /> Guardando...</> : <><i className="fas fa-check" /> Confirmar</>}
              </button>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}
